import { useEffect, useState } from 'react';
import {
  fetchSocialAction,
  markSocialAction,
  planSocialAction,
  type SocialActionState,
} from '../lib/api';

/**
 * Behavioral-activation prompt (Choi et al. 2020).
 *
 * Lives on Home when the user has completed their week-2 UCLA-3 check-in.
 *
 *   - If they have no active action yet, show three suggestion chips +
 *     a short text field and a "Plan it" button.
 *   - If they have an active action (no outcome), show the action and
 *     three outcome buttons: I did it / Not yet / Changed my mind.
 *
 * The day-7 follow-up email at /api/auth/emails/social-action-followup.ts
 * uses the same outcome shape, so this component also acts as the
 * landing target for the deep links in that email (handled by Home).
 */

const SUGGESTIONS = [
  "Sit with someone at lunch I don't usually.",
  "Send one text to someone I haven't talked to in a while.",
  'Walk through a busy part of campus instead of empty paths.',
];

export function SocialActionPrompt({
  deepLinkOutcome,
}: {
  /** Set by Home.tsx when the URL is ?social-action=...&outcome=...; the
   *  component will auto-record that outcome once the active action loads. */
  deepLinkOutcome?: 'done' | 'not_yet' | 'changed_mind';
}) {
  const [state, setState] = useState<SocialActionState | null>(null);
  const [draft, setDraft] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [acknowledged, setAcknowledged] = useState<null | 'done' | 'not_yet' | 'changed_mind'>(
    null,
  );

  useEffect(() => {
    fetchSocialAction()
      .then(setState)
      .catch(() => setError('Could not load your action.'));
  }, []);

  // Deep-link path: if Home received ?outcome=… and we now have an active
  // action, mark it once.
  useEffect(() => {
    if (!deepLinkOutcome || !state?.action || acknowledged) return;
    void mark(state.action.id, deepLinkOutcome);
    // mark() updates `acknowledged`, which prevents re-fire.
  }, [deepLinkOutcome, state, acknowledged]);

  async function plan() {
    const body = draft.trim();
    if (!body) return;
    setBusy(true);
    setError(null);
    const result = await planSocialAction(body);
    if (result.ok) {
      const next = await fetchSocialAction();
      setState(next);
      setDraft('');
    } else {
      setError(result.error);
    }
    setBusy(false);
  }

  async function mark(id: string, outcome: 'done' | 'not_yet' | 'changed_mind') {
    setBusy(true);
    setError(null);
    const result = await markSocialAction(id, outcome);
    if (result.ok) {
      setAcknowledged(outcome);
      const next = await fetchSocialAction();
      setState(next);
    } else {
      setError(result.error);
    }
    setBusy(false);
  }

  if (!state) return null; // loading or error — keep quiet, the section is optional
  if (!state.eligible) return null; // user hasn't done the week_2 check-in

  // If we just acknowledged a deep-link mark, show a brief thank-you.
  if (acknowledged && !state.action) {
    return (
      <section className="bact">
        <h2>A small social action</h2>
        <p className="muted">
          {acknowledged === 'done' && 'Glad to hear it. That counts for more than you think.'}
          {acknowledged === 'not_yet' &&
            'No pressure — sometimes the noticing is enough. We will not nag you about this one again.'}
          {acknowledged === 'changed_mind' &&
            'Understood. Choosing what fits your life is part of the work.'}
        </p>
      </section>
    );
  }

  // Active action exists → follow-up form
  if (state.action) {
    return (
      <section className="bact">
        <h2>A small social action</h2>
        <p className="muted">
          A week ago you planned: <em className="bact-quote">&ldquo;{state.action.body}&rdquo;</em>
        </p>
        <p className="muted">No right answer. How did it go?</p>
        <div className="stack-row bact-actions">
          <button
            type="button"
            disabled={busy}
            onClick={() => state.action && mark(state.action.id, 'done')}
          >
            I did it
          </button>
          <button
            type="button"
            className="secondary"
            disabled={busy}
            onClick={() => state.action && mark(state.action.id, 'not_yet')}
          >
            Not yet
          </button>
          <button
            type="button"
            className="secondary"
            disabled={busy}
            onClick={() => state.action && mark(state.action.id, 'changed_mind')}
          >
            Changed my mind
          </button>
        </div>
        {error && <p className="error">{error}</p>}
      </section>
    );
  }

  // Eligible, no active action → planning form
  return (
    <section className="bact">
      <h2>A small social action</h2>
      <p className="muted">
        Pick one small, real, this-week thing — borrowed from brief behavioral activation, which
        beats friendly visiting on loneliness in trials. Or tap a suggestion to start.
      </p>
      <ul className="bact-suggestions">
        {SUGGESTIONS.map((s) => (
          <li key={s}>
            <button type="button" className="bact-chip" onClick={() => setDraft(s)} disabled={busy}>
              {s}
            </button>
          </li>
        ))}
      </ul>
      <div className="bact-form">
        <textarea
          value={draft}
          maxLength={140}
          placeholder="Pick one small thing for this week"
          onChange={(e) => setDraft(e.target.value)}
          disabled={busy}
        />
        <button type="button" disabled={!draft.trim() || busy} onClick={plan}>
          {busy ? 'Saving…' : 'Plan it'}
        </button>
      </div>
      {error && <p className="error">{error}</p>}
    </section>
  );
}
